import React from 'react';
import { TemplateSummary, TemplatesResponse, Channel, FeatureResolution } from 'types/cx-editor';
import { SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  CircularProgress
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  ChevronDown,
  ChevronUp,
  Info,
  Mail,
  Globe,
  Newspaper,
  MessageSquare,
  Search,
  LayoutGrid
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectLevel1Data, selectLevel2Data, selectFeatureMetricsLoading, selectFeatureMetricsError } from 'store/dataReducers/featureMetricsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { useGlobalConfig } from 'context/GlobalContext';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { createCxApiClient } from 'framework/http/cxEditorClient';

type Props = {
  selectedTemplateKey?: string;
  selectedSectionKey?: string;
  onSelectTemplate: (templateKey: string) => void;
  onSelectSection?: (templateKey: string, sectionKey: string, sectionId: string, sectionName: string) => void;
  isOpen?: boolean;
  showAllFuelTypes?: boolean;
  pilotId?: string; 
}

// Section data type for L2 items
interface SectionItem {
  sectionKey: string;
  sectionId: string; 
  name: string;
  position?: number | null;
}

// Extended template type with sections
interface TemplateWithSections extends TemplateSummary {
  sections: SectionItem[];
  fuelType?: string; // ELECTRIC, GAS, DUAL, etc.
}

// Extended channel type
interface ChannelWithTemplates {
  name: Channel;
  templates: TemplateWithSections[];
}

// Icon mapping for channels
const channelIcons: Record<string, React.ElementType> = {
  Email: Mail,
  HER: Newspaper,
  Web: Globe,
  SMS: MessageSquare,
};

const channelIconFor = (name: string) => {
  const Icon = channelIcons[name];
  return Icon ? <Icon size={16} /> : null;
};

// Transform featureId to display name
// Example: "EMAIL.SF.MONTHLY_SUMMARY" -> "Monthly Summary"
const extractTemplateName = (featureId: string): string => {
  const parts = featureId.split('.');
  const lastPart = parts[parts.length - 1] || featureId;
  return lastPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getSectionDisplayName = (sectionId: string): string => {
  let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionId];
  
  if (!displayName) {
    const baseId = sectionId.replace(/_[Vv]\d+$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
  }
  
  if (!displayName) {
    const cleanId = sectionId.replace(/_[Vv]\d+.*$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
  }
  
  if (!displayName) {
    displayName = sectionId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  return displayName;
};

const VALID_FUEL_TYPES = ['ELECTRIC', 'GAS', 'WATER', 'DUAL', 'DUAL_FUEL', 'DUALFUEL'];

const isValidFuelType = (fuelType?: string | null): boolean => {
  if (!fuelType) return false;
  return VALID_FUEL_TYPES.includes(fuelType.trim().toUpperCase());
};

const normalizeFuelType = (fuelType?: string | null): string => {
  if (!fuelType) return 'ELECTRIC';
  const normalized = fuelType.trim().toUpperCase();
  return isValidFuelType(normalized) ? normalized : 'ELECTRIC';
};

// Convert fuelType to display label
// ELECTRIC -> "EL", GAS -> "GAS", DUAL -> "DF", WATER -> "WA"
const getFuelTypeLabel = (fuelType?: string | null): string => {
  const normalizedFuelType = normalizeFuelType(fuelType);
  switch (normalizedFuelType) {
    case 'ELECTRIC':
      return 'EL';
    case 'GAS':
      return 'GAS';
    case 'WATER':
      return 'WATER';
    case 'DUAL':
    case 'DUAL_FUEL':
    case 'DUALFUEL':
      return 'DF';
    default:
      return 'EL'; // Default to EL
  }
};

// Parse L1 featureId to get base key (remove fuel type suffix if present)
// Example: "EMAIL.DF.BILL_PROJECTION.ELECTRIC" -> "EMAIL.DF.BILL_PROJECTION"
// Example: "EMAIL.DF.USER_WELCOME" -> "EMAIL.DF.USER_WELCOME"
const parseL1BaseKey = (featureId: string): string => {
  const parts = featureId.split('.');
  if (parts.length < 2) return featureId;
  
  const lastPart = parts[parts.length - 1].toUpperCase();
  
  if (VALID_FUEL_TYPES.includes(lastPart)) {
    return parts.slice(0, -1).join('.');
  }
  
  return featureId;
};

// Extract smart display name from featureId
// Handles cases like "EMAIL.DF.USER_WELCOME" -> "User Welcome"
// Also handles "EMAIL.DF.BILL_PROJECTION.ELECTRIC" -> "Bill Projection"
const extractSmartDisplayName = (featureId: string, displayName?: string | null): string => {
  // If displayName exists and looks valid (not too short, not just "Df", etc.)
  if (displayName && displayName.length > 2 && displayName.toLowerCase() !== 'df') {
    return displayName;
  }
  
  const parts = featureId.split('.');
  if (parts.length < 2) return featureId;
  
  // Find the meaningful part (skip product prefix and fuel suffix)
  // Example: EMAIL.DF.USER_WELCOME -> USER_WELCOME
  // Example: EMAIL.DF.BILL_PROJECTION.ELECTRIC -> BILL_PROJECTION
  let meaningfulPart = '';
  
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i].toUpperCase();
    if (VALID_FUEL_TYPES.includes(part) || ['EMAIL', 'WEB', 'HER', 'SMS', 'DF', 'SF'].includes(part)) {
      continue;
    }
    meaningfulPart = parts[i];
    break;
  }
  
  if (!meaningfulPart) {
    meaningfulPart = parts[parts.length - 1];
  }
  
  return meaningfulPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};


const convertBulkManageToWebTemplates = (
  bulkManageData: any[],
  fuelType: string,
  showAllFuelTypes: boolean = false
): TemplateWithSections[] => {
  const templates: TemplateWithSections[] = [];
  const templateMap = new Map<string, TemplateWithSections>();
  
  if (Array.isArray(bulkManageData)) {
    bulkManageData.forEach(item => {
      const productType = item?.productType;
      if (!productType) return;
      
      if (!showAllFuelTypes && item?.fuelType) {
        const itemFuelType = typeof item.fuelType === 'string'
          ? item.fuelType.trim().toUpperCase()
          : String(item.fuelType).trim().toUpperCase();
        if (isValidFuelType(itemFuelType)) {
          const projectFuelType = fuelType.trim().toUpperCase();
          if (itemFuelType !== projectFuelType) {
            return;
          }
        }
      }
      
      const displayName = item?.displayName || extractSmartDisplayName(productType, item?.displayName);
      const templateKey = productType; // Use productType as templateKey
      const rawFuelType = item?.fuelType 
        ? (typeof item.fuelType === 'string' ? item.fuelType : String(item.fuelType))
        : null;
      const normalizedFuelType = normalizeFuelType(rawFuelType);
      
      if (!templateMap.has(templateKey)) {
        templateMap.set(templateKey, {
          templateKey,
          name: displayName,
          channel: 'Web' as Channel,
          sections: [], 
          fuelType: normalizedFuelType
        });
      }
    });
  }
  
  return Array.from(templateMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const transformToHierarchicalData = (
  level1Data: FeatureResolution[],
  level2Data: FeatureResolution[] | null,
  fuelType: string,
  showAllFuelTypes: boolean = false,
  bulkManageData: any[] | null = null
): ChannelWithTemplates[] => {
  if (!Array.isArray(level1Data)) {
    level1Data = [];
  }

  const channelMap = new Map<Channel, Map<string, TemplateWithSections>>();
  
  const l1BaseKeyToTemplateKey = new Map<string, string[]>();

  // Filter L1 ACCESS resolutions
  // When showAllFuelTypes is true, include all fuel types (for Global)
  const l1AccessResolutions = level1Data.filter(r => {
    if (!r || r.resolution !== 'ACCESS') return false;
    // Skip fuel type filtering if showAllFuelTypes is true
    if (showAllFuelTypes) return true;
    if (r.fuelType) {
      const resFuelType = typeof r.fuelType === 'string'
        ? r.fuelType.trim().toUpperCase()
        : String(r.fuelType).trim().toUpperCase();
      if (isValidFuelType(resFuelType)) {
        const projectFuelType = fuelType.trim().toUpperCase();
        return resFuelType === projectFuelType;
      }
    }
    return true;
  });

  // Process L1 templates (EMAIL, HER, SMS only - WEB uses L2 directly)
  l1AccessResolutions.forEach(resolution => {
    if (!resolution || !resolution.featureId) return;

    const productValue = resolution.product;
    if (!productValue || typeof productValue !== 'string') return;

    const product = productValue.trim().toUpperCase();
    let channelName: Channel;

    // Skip WEB for L1 - we'll use L2 data for WEB
    if (product === 'EMAIL') channelName = 'Email';
    else if (product === 'HER') channelName = 'HER';
    else if (product === 'SMS') channelName = 'SMS';
    else if (product === 'WEB') return; // Skip WEB in L1
    else return;

    const templateKey = resolution.featureId;
    const baseKey = parseL1BaseKey(templateKey);
    
    const displayName = extractSmartDisplayName(resolution.featureId, resolution.displayName);
    
    const rawFuelType = resolution.fuelType 
      ? (typeof resolution.fuelType === 'string' ? resolution.fuelType : String(resolution.fuelType))
      : null;
    const normalizedFuelType = normalizeFuelType(rawFuelType);

    if (!channelMap.has(channelName)) {
      channelMap.set(channelName, new Map());
    }

    const templateMap = channelMap.get(channelName)!;
    if (!templateMap.has(templateKey)) {
      templateMap.set(templateKey, {
        templateKey,
        name: displayName,
        channel: channelName,
        sections: [],
        fuelType: normalizedFuelType
      });
      
      if (!l1BaseKeyToTemplateKey.has(baseKey)) {
        l1BaseKeyToTemplateKey.set(baseKey, []);
      }
      l1BaseKeyToTemplateKey.get(baseKey)!.push(templateKey);
    }
  });

  // Process L2 data
  if (level2Data && Array.isArray(level2Data)) {
    // Filter L2 ACCESS resolutions
    const l2AccessResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      // Skip fuel type filtering if showAllFuelTypes is true
      if (showAllFuelTypes) return true;
      if (r.fuelType) {
        const resFuelType = typeof r.fuelType === 'string'
          ? r.fuelType.trim().toUpperCase()
          : String(r.fuelType).trim().toUpperCase();
        if (isValidFuelType(resFuelType)) {
          const projectFuelType = fuelType.trim().toUpperCase();
          return resFuelType === projectFuelType;
        }
      }
      return true;
    });

    l2AccessResolutions.forEach(resolution => {
      const productValue = resolution.product;
      if (!productValue || typeof productValue !== 'string') return;

      const product = productValue.trim().toUpperCase();

      if (product === 'WEB') {
        return;
      }

      // For EMAIL, HER, SMS - attach L2 as sections to L1 templates
      let channelName: Channel;
      if (product === 'EMAIL') channelName = 'Email';
      else if (product === 'HER') channelName = 'HER';
      else if (product === 'SMS') channelName = 'SMS';
      else return;

      if (!resolution.section) return;

      const sectionKey = resolution.featureId || resolution.section;
      const sectionId = resolution.section;
      const sectionName = getSectionDisplayName(sectionId);
      const section: SectionItem = {
        sectionKey,
        sectionId,
        name: sectionName,
        position: resolution.position
      };

      // Match L2 productType with L1 base key
      if (channelMap.has(channelName) && resolution.productType) {
        const templateMap = channelMap.get(channelName)!;
        
        let parentTemplate = templateMap.get(resolution.productType);
        
        if (!parentTemplate) {
          const matchingTemplateKeys = l1BaseKeyToTemplateKey.get(resolution.productType);
          if (matchingTemplateKeys && matchingTemplateKeys.length > 0) {
            const l2FuelType = normalizeFuelType(resolution.fuelType);
            for (const key of matchingTemplateKeys) {
              const template = templateMap.get(key);
              if (template && template.fuelType === l2FuelType) {
                parentTemplate = template;
                break;
              }
            }
            if (!parentTemplate) {
              parentTemplate = templateMap.get(matchingTemplateKeys[0]);
            }
          }
        }
        
        if (parentTemplate && !parentTemplate.sections.find(s => s.sectionKey === sectionKey)) {
          parentTemplate.sections.push(section);
        }
      }
    });
  }


  if (bulkManageData && Array.isArray(bulkManageData) && bulkManageData.length > 0) {
    const webTemplates = convertBulkManageToWebTemplates(bulkManageData, fuelType, showAllFuelTypes);
    
    if (webTemplates.length > 0) {
      const channelName: Channel = 'Web';
      if (!channelMap.has(channelName)) {
        channelMap.set(channelName, new Map());
      }
      const templateMap = channelMap.get(channelName)!;
      webTemplates.forEach(template => {
        if (!templateMap.has(template.templateKey)) {
          templateMap.set(template.templateKey, template);
        }
      });
    }
  } else if (level2Data && Array.isArray(level2Data)) {
    const webL2AccessResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      
      const productValue = r.product;
      if (!productValue || typeof productValue !== 'string') return false;
      if (productValue.trim().toUpperCase() !== 'WEB') return false;
      
      if (!r.productType) {
        return false;
      }
      const productTypeUpper = r.productType.toUpperCase();
      const fuelTypeUpper = fuelType.toUpperCase();
      const startsWithDashboard = productTypeUpper.startsWith('DASHBOARD.');
      const containsFuelType = productTypeUpper.includes('.' + fuelTypeUpper + '.') || 
                                productTypeUpper.endsWith('.' + fuelTypeUpper) ||
                                productTypeUpper === 'DASHBOARD.' + fuelTypeUpper;
      const matches = startsWithDashboard && containsFuelType;
      
      if (!matches) {
        return false;
      }
      
      if (!r.section) {
        return false;
      }
      
      if (showAllFuelTypes) return true;
      if (r.fuelType) {
        const resFuelType = typeof r.fuelType === 'string'
          ? r.fuelType.trim().toUpperCase()
          : String(r.fuelType).trim().toUpperCase();
        if (isValidFuelType(resFuelType)) {
          const projectFuelType = fuelType.trim().toUpperCase();
          return resFuelType === projectFuelType;
        }
      }
      return true;
    });

    webL2AccessResolutions.forEach(resolution => {
      if (!resolution.featureId) return;
      
      const channelName: Channel = 'Web';
      const templateKey = resolution.featureId;
      
      const displayName = resolution.section 
        ? getSectionDisplayName(resolution.section)
        : extractSmartDisplayName(resolution.featureId, resolution.displayName);
      
      const rawFuelType = resolution.fuelType 
        ? (typeof resolution.fuelType === 'string' ? resolution.fuelType : String(resolution.fuelType))
        : null;
      const normalizedFuelType = normalizeFuelType(rawFuelType);

      if (!channelMap.has(channelName)) {
        channelMap.set(channelName, new Map());
      }

      const templateMap = channelMap.get(channelName)!;
      if (!templateMap.has(templateKey)) {
        templateMap.set(templateKey, {
          templateKey,
          name: displayName,
          channel: channelName,
          sections: [],
          fuelType: normalizedFuelType
        });
      }
    });
  }

  // Convert to array and sort
  const channelOrder: Channel[] = ['Email', 'HER', 'SMS', 'Web'];
  const channels: ChannelWithTemplates[] = channelOrder
    .filter(ch => channelMap.has(ch))
    .map(ch => {
      const templateMap = channelMap.get(ch)!;
      const templates = Array.from(templateMap.values())
        .map(t => ({
          ...t,
          sections: t.sections.sort((a, b) => {
            if (a.position !== null && b.position !== null && a.position !== undefined && b.position !== undefined) {
              return a.position - b.position;
            }
            return a.name.localeCompare(b.name);
          })
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      
      return {
        name: ch,
        templates
      };
    })
    .filter(ch => ch.templates.length > 0);

  return channels;
};

export const DataScenariosSidebar: React.FC<Props> = ({
  selectedTemplateKey,
  selectedSectionKey,
  onSelectTemplate,
  onSelectSection,
  isOpen = true,
  showAllFuelTypes = false,
  pilotId
}) => {
  const { rc } = useGlobalConfig();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const effectivePilotId = pilotId || selectedPilotId;
  const level1Data = useSelector(selectLevel1Data);
  const level2Data = useSelector(selectLevel2Data);
  const loading = useSelector(selectFeatureMetricsLoading);
  const error = useSelector(selectFeatureMetricsError);
  const projectAttributes = useSelector(selectProjectAttributes);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [expandedTemplates, setExpandedTemplates] = React.useState<Set<string>>(new Set());
  const [bulkManageData, setBulkManageData] = React.useState<any[] | null>(null);
  const [bulkManageLoading, setBulkManageLoading] = React.useState(false);
  const [bulkManageError, setBulkManageError] = React.useState<string | null>(null);

  const isLoading = loading || level1Data === null || bulkManageLoading;

  // Extract fuelType from projectAttributes
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC';
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);

  // Fetch bulk-manage data for Web channel templates
  React.useEffect(() => {
    if (!effectivePilotId) {
      setBulkManageData(null);
      return;
    }

    let ignore = false;
    async function fetchBulkManage() {
      setBulkManageLoading(true);
      setBulkManageError(null);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const pilotIdStr = String(effectivePilotId);
        const response = await api.getBulkManage(pilotIdStr);
        
        if (!ignore) {
          const data = Array.isArray(response) ? response : (response?.data || response?.items || []);
          setBulkManageData(Array.isArray(data) ? data : []);
        }
      } catch (err: any) {
        if (!ignore) {
          console.error('[DataScenariosSidebar] Failed to fetch bulk-manage data:', err);
          setBulkManageError(err.message || 'Failed to load Web templates');
          setBulkManageData(null);
        }
      } finally {
        if (!ignore) {
          setBulkManageLoading(false);
        }
      }
    }

    fetchBulkManage();
    return () => {
      ignore = true;
    };
  }, [effectivePilotId, rc]);

  // Transform data to hierarchical structure
  const data = React.useMemo(() => {
    // If API data exists, use it
    if (level1Data && Array.isArray(level1Data) && level1Data.length > 0) {
      return transformToHierarchicalData(level1Data, level2Data, fuelType, showAllFuelTypes, bulkManageData);
    }
    return [];
  }, [level1Data, level2Data, fuelType, showAllFuelTypes, bulkManageData]);

  // Filter based on search query
  const filteredData = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return data;
    }

    const query = searchQuery.toLowerCase();
    return data
      .map(channel => ({
        ...channel,
        templates: channel.templates
          .map(template => ({
            ...template,
            sections: template.sections.filter(section =>
              section.name.toLowerCase().includes(query) ||
              section.sectionKey.toLowerCase().includes(query)
            )
          }))
          .filter(template =>
            template.name.toLowerCase().includes(query) ||
            template.templateKey.toLowerCase().includes(query) ||
            template.sections.length > 0
          )
      }))
      .filter(channel => channel.templates.length > 0);
  }, [data, searchQuery]);

  const handleToggleTemplate = (templateKey: string) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateKey)) {
        newSet.delete(templateKey);
      } else {
        newSet.add(templateKey);
      }
      return newSet;
    });
  };

  const handleSelectTemplate = (templateKey: string) => {
    onSelectTemplate(templateKey);
    // Auto-expand when selecting
    setExpandedTemplates(prev => new Set(prev).add(templateKey));
  };

  const handleSelectSection = (templateKey: string, sectionKey: string, sectionId: string, sectionName: string) => {
    onSelectSection?.(templateKey, sectionKey, sectionId, sectionName);
  };

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 2, width: '100%', minWidth: isOpen ? 300 : 48, overflow: 'hidden' }}>
      <CardHeader
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isOpen && (
              <>
                <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  Templates
                </Typography>
                <Tooltip title="Select a template to view and manage its data scenarios.">
                  <IconButton 
                    size="small" 
                    sx={{ 
                      p: 0.5,
                      color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit'
                    }}
                  >
                    <Info size={16} style={{ opacity: 0.7 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        }
      />
      <CardContent sx={{ pt: 1.5, px: 2, pb: 2, flex: 1, display: isOpen ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {isLoading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 6,
              gap: 2
            }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading templates...
              </Typography>
            </Box>
          )}
          {(error || bulkManageError) && !isLoading && (
            <Typography variant="body2" color="error" sx={{ py: 2, px: 1 }}>
              {error || bulkManageError}
            </Typography>
          )}

          {!isLoading && !error && (
            <>
              <TextField
                placeholder="Search templates"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} style={{ opacity: 0.7 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  mb: 1.5,
                  '& .MuiInputAdornment-root svg': {
                    color: theme => theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.secondary
                  }
                }}
              />

              {filteredData.length === 0 && (
                <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates available. Please select a project from Application Scope.
                  </Typography>
                </Box>
              )}

              {filteredData.map(channel => (
                <Accordion
                  key={channel.name}
                  disableGutters
                  elevation={0}
                  defaultExpanded={channel.name === 'Email'}
                  sx={{
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown size={16} style={{ color: 'inherit' }} />}
                    sx={{
                      minHeight: 40,
                      '&.Mui-expanded': { minHeight: 40 },
                      px: 1,
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: theme => theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.secondary
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}
                    >
                      {channelIconFor(channel.name)}
                      {channel.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {channel.templates.map((template) => {
                        const isTemplateSelected = template.templateKey === selectedTemplateKey && !selectedSectionKey;
                        const isTemplateExpanded = expandedTemplates.has(template.templateKey);
                        const hasSections = template.sections.length > 0;

                        return (
                          <Box key={template.templateKey}>
                            {/* Template Row with Tooltip showing featureId */}
                            <Tooltip 
                              title={`${template.templateKey}`} 
                              placement="right"
                              arrow
                              enterDelay={500}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  px: 1.5,
                                  py: 1,
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  border: isTemplateSelected
                                    ? theme => `1px solid ${theme.palette.primary.main}`
                                    : '1px solid transparent',
                                  backgroundColor: isTemplateSelected
                                    ? theme => theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.16)
                                      : alpha(theme.palette.primary.main, 0.08)
                                    : 'transparent',
                                  '&:hover': {
                                    backgroundColor: theme => theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.common.white, 0.08)
                                      : alpha(theme.palette.common.black, 0.04)
                                  },
                                  transition: 'all 0.2s ease-in-out'
                                }}
                                onClick={() => handleSelectTemplate(template.templateKey)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={getFuelTypeLabel(template.fuelType)}
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: 20,
                                      minWidth: 42,
                                      px: 0.5,
                                      py: 0.5,
                                      borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                      color: 'primary.main',
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                      flexShrink: 0,
                                      '& .MuiChip-label': {
                                        px: 1,
                                        textAlign: 'center'
                                      }
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1,
                                      fontSize: '0.875rem',
                                      textAlign: 'left'
                                    }}
                                  >
                                    {template.name}
                                  </Typography>
                                </Box>
                                {hasSections && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleTemplate(template.templateKey);
                                    }}
                                    sx={{ 
                                      p: 0.5,
                                      color: theme => theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.secondary
                                    }}
                                  >
                                    {isTemplateExpanded ? (
                                      <ChevronUp size={16} />
                                    ) : (
                                      <ChevronDown size={16} />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                            </Tooltip>

                            {/* Sections List (nested under template) */}
                            {hasSections && isTemplateExpanded && (
                              <Box
                                sx={{
                                  ml: 2,
                                  mt: 0.5,
                                  borderLeft: theme => `1px solid ${theme.palette.divider}`,
                                  pl: 1
                                }}
                              >
                                {template.sections.map((section) => {
                                  const isSectionSelected =
                                    template.templateKey === selectedTemplateKey &&
                                    section.sectionKey === selectedSectionKey;

                                  return (
                                    <Tooltip
                                      key={section.sectionKey}
                                      title={section.sectionId}
                                      placement="right"
                                      arrow
                                      enterDelay={500}
                                    >
                                      <Box
                                        onClick={() => handleSelectSection(template.templateKey, section.sectionKey, section.sectionId, section.name)}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          px: 1.5,
                                          py: 0.75,
                                          cursor: 'pointer',
                                          borderRadius: 1,
                                          border: isSectionSelected
                                            ? theme => `1px solid ${theme.palette.primary.main}`
                                            : '1px solid transparent',
                                          backgroundColor: isSectionSelected
                                            ? theme => theme.palette.mode === 'dark'
                                              ? alpha(theme.palette.primary.main, 0.16)
                                              : alpha(theme.palette.primary.main, 0.08)
                                            : 'transparent',
                                          '&:hover': {
                                            backgroundColor: theme => theme.palette.mode === 'dark'
                                              ? alpha(theme.palette.common.white, 0.08)
                                              : alpha(theme.palette.common.black, 0.04)
                                          },
                                          transition: 'all 0.2s ease-in-out'
                                        }}
                                      >
                                        <LayoutGrid size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.8125rem',
                                            textAlign: 'left'
                                          }}
                                        >
                                          {section.name}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

